import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, RotateCcw, Ban } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const BRIDGE = `${API}/api/admin/media-bridge`;

/**
 * AdminMediaBridgeJobDetail — Phase 1 detail view for a single
 * media job. Source-pinned by
 * `/app/backend/tests/test_media_bridge_doctrine.py`.
 *
 * Two actions only: per-job retry (resets capped derivatives) and
 * per-job cancel (moves non-terminal → CANCELLED). No bulk actions.
 */
export default function AdminMediaBridgeJobDetail() {
  const { jobId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setErr(null);
    try {
      const r = await axios.get(`${BRIDGE}/job/${jobId}`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.status === 404
        ? "Job not found OR Media Bridge is OFF."
        : (e?.response?.data?.detail || "Failed to load."));
    }
  };

  useEffect(() => { load(); }, [jobId]);

  const act = async (action) => {
    setBusy(true);
    try {
      await axios.post(`${BRIDGE}/job/${jobId}/${action}`, {},
        { withCredentials: true });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || `${action} failed.`);
    } finally {
      setBusy(false);
    }
  };

  const j = data?.job;
  const terminal = j && ["ready_for_review", "failed", "cancelled"].includes(j.status);

  return (
    <div data-testid="admin-media-bridge-job" className="min-h-dvh bg-slate-50 dark:bg-slate-950 pb-12">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          <Link to="/admin/media-bridge" data-testid="job-back" className="text-slate-500 hover:text-slate-900">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <p className="font-extrabold text-slate-900 dark:text-white">Media Job · {(jobId || "").slice(0, 10)}</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 pt-5 space-y-4">
        {err && (
          <div data-testid="job-error" className="rounded-xl border border-amber-300 bg-amber-50 text-amber-900 p-3 text-sm">{err}</div>
        )}

        {j && (
          <section data-testid="job-summary" className="rounded-xl border border-slate-200 bg-white dark:bg-slate-900 p-4">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <dt className="text-slate-500">Job ID</dt>
              <dd className="font-mono text-xs">{j.id}</dd>
              <dt className="text-slate-500">Status</dt>
              <dd data-testid="job-status" className="font-mono">{j.status}</dd>
              <dt className="text-slate-500">Persona / purpose</dt>
              <dd className="font-mono">{j.persona} · {j.purpose}</dd>
              <dt className="text-slate-500">Age (idle)</dt>
              <dd>{Math.round((j.age_seconds || 0) / 60)} min</dd>
              <dt className="text-slate-500">State version</dt>
              <dd className="font-mono">{j.state_version}</dd>
            </dl>

            <div className="mt-3 border-t pt-3">
              <p className="text-xs font-bold text-slate-600 mb-1">Derivatives</p>
              <ul className="text-xs font-mono space-y-0.5">
                {Object.entries(j.derivatives || {}).map(([k, v]) => (
                  <li key={k}>
                    {k}: <span className="text-slate-700">{v.status || "—"}</span>{" "}
                    <span className="text-slate-400">attempts={v.attempt_count ?? 0}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-4 flex gap-2">
              <button
                data-testid="job-retry-btn"
                disabled={busy || terminal}
                onClick={() => act("retry")}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-bold disabled:opacity-40"
              ><RotateCcw className="w-3.5 h-3.5" /> Safe retry</button>
              <button
                data-testid="job-cancel-btn"
                disabled={busy || terminal}
                onClick={() => act("cancel")}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-700 text-white text-xs font-bold disabled:opacity-40"
              ><Ban className="w-3.5 h-3.5" /> Cancel</button>
              {terminal && <span className="text-[11px] text-slate-400 self-center">Job is terminal — actions disabled.</span>}
            </div>
          </section>
        )}

        {data?.audit?.length > 0 && (
          <section data-testid="job-audit" className="rounded-xl border border-slate-200 bg-white dark:bg-slate-900 p-4">
            <p className="font-bold text-slate-900 dark:text-white mb-2 text-sm">Audit for this job</p>
            <ul className="text-xs font-mono space-y-0.5">
              {data.audit.map((r) => (
                <li key={r.id}>
                  <span className="text-slate-500">{(r.at || "").slice(11, 19)}</span>{" "}
                  <span className="font-bold">{r.action}</span>{" "}
                  <span className="text-slate-400">· {r.reason}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
}
